/**
 * @file inward-grn.ts
 * @module app/modules/inward/utils/inward-grn
 * @description Pure state and payload helpers for the Inward "Create GRN" form. The rules
 *   match the server (`backend/src/_platforms/_product/utils/inward.ts`) so the form blocks
 *   what the API would reject.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { getPendingCount, InwardPoQuantityType, unitLabel } from "./inward-po";

/** Backend ReasonStatus: Missing / Damaged are terminal, Delayed stays pending on the PO. */
export const REASON_MISSING = 1;
export const REASON_DAMAGED = 2;
export const REASON_DELAYED = 3;

/** "Carton with Pieces (CWP)" etc. */
export const quantityTypeLabel = unitLabel;

/**
 * What the loading bay manager enters. The gate entry number comes from the PO assignment;
 * invoice / receipt numbers and the receipt verification code are entered by Finance.
 */
export interface GrnHeader {
  driverName: string;
  driverPhone: string;
  vehicleNumber: string;
  /** datetime-local value, e.g. 2026-10-08T14:30 (East Africa Time) */
  receiptDate: string;
}

export const GRN_HEADER_FIELDS: {
  key: keyof GrnHeader;
  label: string;
  type?: string;
}[] = [
  { key: "driverName", label: "Driver name" },
  { key: "driverPhone", label: "Driver phone", type: "tel" },
  { key: "vehicleNumber", label: "Vehicle number" },
  { key: "receiptDate", label: "Delivery date & time", type: "datetime-local" },
];

export const emptyHeader = (receiptDate: string): GrnHeader => ({
  driverName: "",
  driverPhone: "",
  vehicleNumber: "",
  receiptDate,
});

export interface GrnBatchRow {
  batch: number;
  /** text, so the input can be cleared while typing */
  stockCount: string;
  /** date input value, YYYY-MM-DD */
  expiry: string;
}

export interface GrnTypeState {
  type: number;
  ordered: number;
  received: number;
  rejected: number;
  pending: number;
  rows: GrnBatchRow[];
  /** not delivered at all (text so the input can be cleared) */
  missing: string;
  /** delivered damaged */
  damaged: string;
}

export interface GrnVariantState {
  variantId: string;
  title: string;
  image?: string;
  types: GrnTypeState[];
}

export interface PoForGrnVariant {
  variant: string | { _id: string; title?: string; media?: { url?: string }[] };
  variantName?: string;
  productName?: string;
  quantityTypes?: InwardPoQuantityType[];
}

const variantIdOf = (v: PoForGrnVariant["variant"]): string =>
  typeof v === "string" ? v : v?._id;

/**
 * One card per PO product with something still pending. Each pending quantity type starts
 * with one batch (the next batch number for that product) and an EMPTY quantity: the loading
 * bay manager types what actually arrived (never pre-filled with the ordered / pending qty).
 */
export const buildInitialVariants = (
  poVariants: PoForGrnVariant[],
  nextBatches: { variant: string; batch: number }[]
): GrnVariantState[] =>
  poVariants
    .map((pv) => {
      const variantId = variantIdOf(pv.variant);
      const firstBatch =
        nextBatches.find((b) => String(b.variant) === String(variantId))
          ?.batch || 1;
      const variantObj =
        typeof pv.variant === "string" ? undefined : pv.variant;
      return {
        variantId,
        title: pv.variantName || variantObj?.title || pv.productName || "-",
        image: variantObj?.media?.[0]?.url,
        types: (pv.quantityTypes ?? [])
          .map((qt) => ({
            type: Number(qt.type),
            ordered: Number(qt.stockCount) || 0,
            received: Number(qt.receivedStockCount) || 0,
            rejected: Number(qt.rejectedStockCount) || 0,
            pending: getPendingCount(qt),
            rows: [
              {
                batch: firstBatch,
                stockCount: "",
                expiry: "",
              },
            ],
            missing: "",
            damaged: "",
          }))
          .filter((t) => t.pending > 0),
      };
    })
    .filter((v) => v.types.length > 0);

export const sumReceived = (t: GrnTypeState): number =>
  t.rows.reduce((total, row) => total + (Number(row.stockCount) || 0), 0);

export const missingOf = (t: GrnTypeState): number => Number(t.missing) || 0;
export const damagedOf = (t: GrnTypeState): number => Number(t.damaged) || 0;

/** Still pending after this GRN (delayed: can come on a later GRN). */
export const remainingAfter = (t: GrnTypeState): number =>
  Math.max(0, t.pending - sumReceived(t) - missingOf(t) - damagedOf(t));

export const isShort = (t: GrnTypeState): boolean => sumReceived(t) < t.pending;

/** Next batch number for a new row of this quantity type. */
export const nextBatchNumber = (t: GrnTypeState): number =>
  Math.max(...t.rows.map((r) => r.batch)) + 1;

const isWholeNumber = (value: string) => /^\d+$/.test(value.trim());

/** First problem in the form, or null when it can be submitted. */
export const validateGrnForm = (
  header: GrnHeader,
  variants: GrnVariantState[]
): string | null => {
  const missing = GRN_HEADER_FIELDS.find((f) => !header[f.key]?.trim());
  if (missing) return `${missing.label} is required`;

  let total = 0;
  for (const v of variants) {
    for (const t of v.types) {
      for (const row of t.rows) {
        if (row.stockCount.trim() === "") {
          return `${v.title} (${quantityTypeLabel(
            t.type
          )}): enter the quantity received for batch ${
            row.batch
          } (0 if nothing came)`;
        }
        if (!isWholeNumber(row.stockCount)) {
          return `${v.title}: quantity must be a whole number`;
        }
        if (Number(row.stockCount) > 0 && !row.expiry) {
          return `${v.title}: select an expiry date for batch ${row.batch}`;
        }
      }
      for (const [label, value] of [
        ["missing", t.missing],
        ["damaged", t.damaged],
      ]) {
        if (value.trim() !== "" && !isWholeNumber(value)) {
          return `${v.title}: ${label} must be a whole number`;
        }
      }
      const received = sumReceived(t);
      const accounted = received + missingOf(t) + damagedOf(t);
      if (accounted > t.pending) {
        return `${v.title} (${quantityTypeLabel(
          t.type
        )}): received + missing + damaged (${accounted}) is more than the ${
          t.pending
        } pending`;
      }
      total += received;
    }
  }
  if (total <= 0)
    return "Enter a received quantity greater than 0 for at least one product";
  return null;
};

const EAT = "+03:00";

/** Reason sent for a short line: Damaged, else Missing, else Delayed. */
export const shortageReason = (t: GrnTypeState): number =>
  damagedOf(t) > 0
    ? REASON_DAMAGED
    : missingOf(t) > 0
    ? REASON_MISSING
    : REASON_DELAYED;

/** Request body for POST _product/inward/purchase-orders/:id/grn. */
export const buildGrnPayload = (
  header: GrnHeader,
  variants: GrnVariantState[]
) => ({
  ...header,
  receiptDate: `${header.receiptDate}:00.000${EAT}`,
  variants: variants.map((v) => ({
    variant: v.variantId,
    quantityTypes: v.types.flatMap((t) => {
      const shortage = isShort(t)
        ? {
            reason: shortageReason(t),
            ...(missingOf(t) > 0 ? { missingStockCount: missingOf(t) } : {}),
            ...(damagedOf(t) > 0 ? { damagedStockCount: damagedOf(t) } : {}),
          }
        : {};
      const lines = t.rows
        .filter((row) => Number(row.stockCount) > 0)
        .map((row) => ({
          type: t.type,
          stockCount: Number(row.stockCount),
          batch: row.batch,
          expiry: `${row.expiry}T00:00:00.000${EAT}`,
        }));
      if (lines.length === 0) {
        return isShort(t) ? [{ type: t.type, stockCount: 0, ...shortage }] : [];
      }
      lines[0] = { ...lines[0], ...shortage };
      return lines;
    }),
  })),
});

/** Current time as a datetime-local value in East Africa Time. */
export const nowInEat = (now: Date = new Date()): string =>
  new Date(now.getTime() + 3 * 60 * 60 * 1000).toISOString().slice(0, 16);
